import {
    createMockMsp,
    createMockManagedOrg,
    createMockSite,
    createMockStandaloneOrg,
} from "stores/slice/bogus/scopeLevel/const";
import {
    MSP_DAILY_CREDITS,
    MSP_MINIMUM_CREDIT_DAYS,
} from "const/license";

const scopeLevelConfig = {
    // Check the initial MSP wallet before the enterprise is created.
    // `licenseKeys` are upper-case and have no separators.
    "/ents/preconfirm.read": {
        protocol: "ws",
        resource: "/ents/preconfirm",
        restful: true,
        method: "read",
        buildParams: null,
        action: null,
        mockHandler: ({ params }) => {
            const dailyRate = MSP_DAILY_CREDITS;
            const remainingDays = MSP_MINIMUM_CREDIT_DAYS;
            const requestedKeys = params?.licenseKeys || [];

            const licenseKeys = requestedKeys.map((id) => {
                const normalizedId = String(id || "").toUpperCase().replace(/-/g, "");
                const isInvalid = normalizedId.includes("INVALID")
                    || normalizedId.includes("MOCKFAIL")
                    || normalizedId === "00000000000000000000"
                    || normalizedId === "11111111111111111111";

                return {
                    id: normalizedId,
                    point: isInvalid ? 0 : 2000,
                    status: isInvalid ? "INVALID" : "VALID",
                };
            });
            const redeemedCredits = licenseKeys.reduce(
                (total, key) => total + (key.status === "VALID" ? key.point : 0),
                0,
            );

            return {
                data: {
                    rate: `${MSP_DAILY_CREDITS} credit/day`,
                    dailyRate,
                    remainingDays,
                    sufficientCredits: redeemedCredits >= dailyRate * remainingDays,
                    licenseKeys,
                },
            };
        },
    },
    // realm get msp & standalone org list
    "/me/memberships.read": {
        protocol: "ws",
        resource: "/me/memberships",
        restful: true,
        method: "read",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.memberships",
    },
    // Surveillance Realm Home memberships. This has the same hierarchy and
    // role visibility as Network memberships, but returns camera/NVR totals.
    "/me/memberships/surveillance.read": {
        protocol: "ws",
        resource: "/me/memberships/surveillance",
        restful: true,
        method: "read",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.memberships",
    },
    // `data` contains `entName` and the validated, normalized `licenseKeys`.
    "/ents.create": {
        protocol: "ws",
        resource: "/ents",
        restful: true,
        method: "create",
        buildParams: null,
        action: createMockMsp,
        mockSelector: "scopeLevel.memberships",
    },
    // realm get msp/org list
    "/ents/:entId/orgs.read": {
        protocol: "ws",
        resource: "/ents/:entId/orgs",
        restful: true,
        method: "read",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.organizations",
        mockHandler: ({ state, restfulPlaceholders }) => (
            state.scopeLevel.organizationsByEnterprise[restfulPlaceholders?.entId]
            || { data: { orgs: [] } }
        ),
    },
    // realm get msp/org/site list
    "/ents/:entId/orgs/:orgId/sites.read": {
        protocol: "ws",
        resource: "/ents/:entId/orgs/:orgId/sites",
        restful: true,
        method: "read",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.data",
        mockHandler: ({ state, restfulPlaceholders }) => (
            state.scopeLevel.sitesByOrganization[restfulPlaceholders?.orgId]
            || { data: { sites: [] } }
        ),
    },
    // realm get org/site list
    "/orgs/:orgId/sites.read": {
        protocol: "ws",
        resource: "/orgs/:orgId/sites",
        restful: true,
        method: "read",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.data",
        mockHandler: ({ state, restfulPlaceholders }) => (
            state.scopeLevel.sitesByOrganization[restfulPlaceholders?.orgId]
            || { data: { sites: [] } }
        ),
    },
    // API/gluon/organizations => /orgs (Create standalone)
    "/orgs.create": {
        protocol: "ws",
        resource: "/orgs",
        restful: true,
        method: "create",
        buildParams: null,
        action: createMockStandaloneOrg,
        mockSelector: "scopeLevel.memberships",
    },
    // API/gluon/enterprise => /ents/{entId}/orgs
    "/ents/:entId/orgs.create": {
        protocol: "ws",
        resource: "/ents/:entId/orgs",
        restful: true,
        method: "create",
        buildParams: null,
        action: createMockManagedOrg,
        mockSelector: "scopeLevel.memberships",
    },
    // API/gluon/organizations => /ents/{entId}/orgs/{orgId}/sites
    "/ents/:entId/orgs/:orgId/sites.create": {
        protocol: "ws",
        resource: "/ents/:entId/orgs/:orgId/sites",
        restful: true,
        method: "create",
        buildParams: null,
        action: createMockSite,
        mockSelector: "scopeLevel.data",
    },
    // API/gluon/organizations => /orgs/{orgId}/sites (standalone)
    "/orgs/:orgId/sites.create": {
        protocol: "ws",
        resource: "/orgs/:orgId/sites",
        restful: true,
        method: "create",
        buildParams: null,
        action: createMockSite,
        mockSelector: "scopeLevel.data",
    },
    // Provisional Clone Site endpoints. Replace resource paths and payloads
    // when the backend API specification is available.
    "/orgs/:orgId/sites/:siteId/clone-source.read": {
        protocol: "ws",
        resource: "/orgs/:orgId/sites/:siteId/clone-source",
        restful: true,
        method: "read",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.cloneSource",
    },
    "/ents/:entId/orgs/:orgId/sites/:siteId/clone-source.read": {
        protocol: "ws",
        resource: "/ents/:entId/orgs/:orgId/sites/:siteId/clone-source",
        restful: true,
        method: "read",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.cloneSource",
    },
    "/orgs/:orgId/sites/name-availability.read": {
        protocol: "ws",
        resource: "/orgs/:orgId/sites/name-availability",
        restful: true,
        method: "read",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.cloneNameCheck",
    },
    "/ents/:entId/orgs/:orgId/sites/name-availability.read": {
        protocol: "ws",
        resource: "/ents/:entId/orgs/:orgId/sites/name-availability",
        restful: true,
        method: "read",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.cloneNameCheck",
    },
    "/orgs/:orgId/sites/clone.create": {
        protocol: "ws",
        resource: "/orgs/:orgId/sites/clone",
        restful: true,
        method: "create",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.cloneJob",
    },
    "/ents/:entId/orgs/:orgId/sites/clone.create": {
        protocol: "ws",
        resource: "/ents/:entId/orgs/:orgId/sites/clone",
        restful: true,
        method: "create",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.cloneJob",
    },
    "/clone-jobs/:jobId.read": {
        protocol: "ws",
        resource: "/clone-jobs/:jobId",
        restful: true,
        method: "read",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.cloneJob",
    },
    "/orgs/:orgId/sites/:sourceSiteId/clonePlan.read": {
        protocol: "ws",
        resource: "/orgs/:orgId/sites/:sourceSiteId/clonePlan",
        restful: true,
        method: "read",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.clonePlan",
    },
    "/ents/:entId/orgs/:orgId/sites/:sourceSiteId/clonePlan.read": {
        protocol: "ws",
        resource: "/ents/:entId/orgs/:orgId/sites/:sourceSiteId/clonePlan",
        restful: true,
        method: "read",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.clonePlan",
    },
    "/orgs/:orgId/siteClones.create": {
        protocol: "ws",
        resource: "/orgs/:orgId/siteClones",
        restful: true,
        method: "create",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.siteClones",
    },
    "/ents/:entId/orgs/:orgId/siteClones.create": {
        protocol: "ws",
        resource: "/ents/:entId/orgs/:orgId/siteClones",
        restful: true,
        method: "create",
        buildParams: null,
        action: null,
        mockSelector: "scopeLevel.siteClones",
    },


};

export default scopeLevelConfig;
